import { useReverification } from "@tribunalea/auth/react";
import {
  Building2,
  CheckCircle2,
  Gift,
  Search,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const grantOptions = [500, 1_000, 5_000];
const creditNumber = new Intl.NumberFormat("es-AR");

function newRequestId() {
  return globalThis.crypto.randomUUID();
}

function TargetIcon({ target }) {
  if (target.imageUrl) return <img src={target.imageUrl} alt="" />;
  const Icon = target.type === "organization" ? Building2 : UserRound;
  return <span className="credit-grant-avatar"><Icon size={19} aria-hidden="true" /></span>;
}

export function AdminCreditGrantPage({ api, access }) {
  const createVerifiedGrant = useReverification(api.createFreeCreditGrant);
  const request = useRef(null);
  const [query, setQuery] = useState("");
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [amount, setAmount] = useState("500");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    request.current = null;
  }, [amount, reason, selectedTarget]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setTargets([]);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const results = await api.searchCreditGrantTargets(normalizedQuery);
        if (active) setTargets(results);
      } catch (searchError) {
        if (active) {
          setTargets([]);
          setError(searchError.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [api, query]);

  const selectTarget = (target) => {
    if (target.disabled) return;
    setSelectedTarget(target);
    setSuccess(null);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const credits = Number(amount);
    if (!selectedTarget) {
      setError("Elegí una persona o una organización.");
      return;
    }
    if (!Number.isInteger(credits) || credits < 1 || credits > 1_000_000) {
      setError("Ingresá entre 1 y 1.000.000 créditos enteros.");
      return;
    }
    const normalizedReason = reason.trim();
    if (normalizedReason.length < 5) {
      setError("Indicá un motivo de al menos 5 caracteres.");
      return;
    }
    if (!window.confirm(
      `Vas a otorgar ${creditNumber.format(credits)} créditos sin cobro a ${selectedTarget.name}. ¿Continuar?`,
    )) return;

    const fingerprint = [
      selectedTarget.type,
      selectedTarget.id,
      credits,
      normalizedReason,
    ].join(":");
    if (request.current?.fingerprint !== fingerprint) {
      request.current = { fingerprint, id: newRequestId() };
    }

    setSubmitting(true);
    setError("");
    setSuccess(null);
    try {
      const result = await createVerifiedGrant({
        targetType: selectedTarget.type,
        targetId: selectedTarget.id,
        credits,
        reason: normalizedReason,
        requestId: request.current.id,
      });
      setSuccess(result);
      request.current = null;
      setReason("");
    } catch (grantError) {
      if (grantError?.code !== "reverification_cancelled") {
        setError(grantError.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="credit-grant-page">
      <header className="support-access-heading">
        <span className="support-access-eyebrow">CUENTA · ADMINISTRACIÓN INTERNA</span>
        <h1>Otorgar créditos</h1>
        <p>
          Agregá saldo sin cobro a una cuenta personal o a una organización.
          Cada otorgamiento conserva el administrador, el motivo y un identificador único.
        </p>
      </header>

      <section className="support-access-notice">
        <ShieldCheck size={20} aria-hidden="true" />
        <span>
          <strong>Operación protegida y auditable</strong>
          Antes de aplicar el saldo se confirmará tu identidad. Los créditos se
          agregan al balance actual y no generan un pago.
        </span>
      </section>

      {access?.requiresReverification ? (
        <div className="credit-grant-reverification" role="status">
          La próxima confirmación abrirá la verificación de seguridad.
        </div>
      ) : null}
      {error ? <div className="support-access-error" role="alert">{error}</div> : null}
      {success ? (
        <div className="credit-grant-success" role="status">
          <CheckCircle2 size={19} aria-hidden="true" />
          <span>
            <strong>
              {success.applied
                ? `${creditNumber.format(success.credits)} créditos otorgados`
                : "Este otorgamiento ya había sido procesado"}
            </strong>
            {success.target.name} ahora tiene {creditNumber.format(success.account.balance)} créditos disponibles.
          </span>
        </div>
      ) : null}

      <div className="credit-grant-layout">
        <section className="credit-grant-search-card">
          <header>
            <h2>1. Elegí el destino</h2>
            <p>Buscá por nombre, email o identificador de Clerk.</p>
          </header>
          <label className="support-access-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Persona u organización"
              autoComplete="off"
            />
          </label>
          <div className="credit-grant-results" aria-live="polite">
            {loading ? (
              <div className="support-access-state">Buscando destinos…</div>
            ) : query.trim().length < 2 ? (
              <div className="support-access-state">Escribí al menos 2 caracteres para buscar.</div>
            ) : targets.length ? targets.map((target) => (
              <button
                type="button"
                className={selectedTarget?.id === target.id
                  && selectedTarget?.type === target.type ? "is-selected" : ""}
                key={`${target.type}:${target.id}`}
                disabled={target.disabled}
                onClick={() => selectTarget(target)}
              >
                <TargetIcon target={target} />
                <span>
                  <strong>{target.name}</strong>
                  <small>{target.detail}</small>
                </span>
                <em>{target.type === "organization" ? "Organización" : "Persona"}</em>
              </button>
            )) : (
              <div className="support-access-state">No encontramos coincidencias.</div>
            )}
          </div>
        </section>

        <form className="credit-grant-form" onSubmit={submit}>
          <header>
            <span><Gift size={21} aria-hidden="true" /></span>
            <div>
              <h2>2. Definí el otorgamiento</h2>
              <p>El saldo se acredita inmediatamente.</p>
            </div>
          </header>

          <div className="credit-grant-selected">
            {selectedTarget ? (
              <>
                <TargetIcon target={selectedTarget} />
                <span>
                  <small>Destino seleccionado</small>
                  <strong>{selectedTarget.name}</strong>
                  <em>{selectedTarget.detail}</em>
                </span>
              </>
            ) : <span>Elegí un destino en los resultados de búsqueda.</span>}
          </div>

          <fieldset>
            <legend>Cantidad de créditos</legend>
            <div className="credit-grant-options">
              {grantOptions.map((option) => (
                <button
                  type="button"
                  className={Number(amount) === option ? "is-selected" : ""}
                  key={option}
                  onClick={() => setAmount(String(option))}
                >
                  {creditNumber.format(option)}
                </button>
              ))}
            </div>
            <label>
              Otra cantidad
              <input
                type="number"
                min="1"
                max="1000000"
                step="1"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                required
              />
            </label>
          </fieldset>

          <label>
            Motivo interno
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={5}
              maxLength={240}
              placeholder="Ej. Crédito de cortesía por incidencia de servicio"
              required
            />
            <small>Quedará guardado en el registro de la operación.</small>
          </label>

          <button type="submit" disabled={!selectedTarget || submitting}>
            <Gift size={17} aria-hidden="true" />
            {submitting ? "Otorgando…" : "Otorgar sin cobro"}
          </button>
        </form>
      </div>
    </div>
  );
}

